/**
 * Inbox draw gate (CLAUDE.md §40) — ONE pure answer to the question "is this
 * record still sealed for this active role?"
 *
 * A card in the Inbox column is visible to the role that works it, but its
 * contents (brief, voice note, tasks, notes, attachments, notepad) stay
 * sealed until that role DRAWS the card and it moves to In Progress:
 *
 *   file folder          → sealed for the Administration Assistant
 *                          ('assistant') until "Draw File"
 *   preparation request  → sealed for the New Business Assistant
 *                          ('new_business_assistant') until "Start preparation"
 *
 * Every other role — the Adviser above all — is never sealed, and nothing is
 * sealed once the record has left Inbox (stored status 'submitted', §7.1).
 * A request reverted to Inbox re-seals. Test records are sealed exactly like
 * real ones.
 *
 * This is an app-level UI gate keyed off the ACTIVE role (from useAuth()) —
 * the same class of rule as the 22 September 2026 Inbox visibility decision
 * (§36.7). It is not RLS and it is not a data change. Every consumer (the
 * two detail views, the pipeline board's note-badge suppression and the
 * login catch-up gate) calls this one function so the rule can never drift
 * between them.
 */

// The stored status value of the Inbox column. "Inbox" is a DISPLAY label
// over an unchanged internal value (CLAUDE.md §7.1) — never rename it.
export const INBOX_SEALED_STATUS = 'submitted';

// Which active role each record kind is sealed for.
export const SEALED_ROLE_BY_KIND = Object.freeze({
  file: 'assistant',
  prep: 'new_business_assistant',
});

/**
 * @param {'file'|'prep'} kind  the record kind
 * @param {string} status       the record's stored status value
 * @param {string} role         the ACTIVE role
 * @returns {boolean} true when the record's contents are sealed for this role
 */
export function isSealedInInbox(kind, status, role) {
  const sealedRole = SEALED_ROLE_BY_KIND[kind];
  if (!sealedRole) return false;
  return status === INBOX_SEALED_STATUS && role === sealedRole;
}
